// İSG Portal — Ayarlar (Settings — tenant-scoped) API çağrıları + tipleri.
//
// KAYNAK: api-contract.md §10 (Ayarlar — tenant-scoped). Backend kanonu; burada
// **varsayım YOK** — endpoint yolları, istek/yanıt alanları ve kimlik kuralları
// (Result unwrap `value`; Create → `value.uuid`; liste → `value` + `total`/
// `filteredTotal`) doğrudan sözleşmeden türetilmiştir. Serileştirme camelCase'dir (§1).
//
// **Ayarlar ≠ Admin (§9).** §9 (`admin.ts`) sistem-admin'in TÜM firmaları yönettiği
// cross-tenant yüzeydir (`/admin/*`, firma `uuid` yolda). §10 ise giriş yapan
// principal'ın **kendi firmasına** (token `CompanyId`) kapanmış Ayarlar'dır: firma
// tekildir (route `uuid` almaz), şube/saha CRUD firmaya kapsanmıştır. İstemci hiçbir
// yerde firma `uuid`'si göndermez — sunucu token'dan çözer (§4b). Bu yüzden yollar
// düzdür (`/company`, `/branches`, `/sites`); admin'in iç-içe yolları burada YOKTUR.

import { api } from './client';
import type { Empty } from './envelope';

// ============================================================================
// Ortak liste parametreleri (api-contract §3 — page/pageSize/search/sort)
// ============================================================================

/** Sayfalı + aranabilir + sıralı liste istek parametreleri (api-contract §3). */
export interface SettingsListParams {
  page?: number;
  pageSize?: number;
  search?: string;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
}

/** `SettingsListParams`'ı client `searchParams` şekline indirger (undefined atlanır). */
function listParams(params: SettingsListParams): Record<string, string | number | undefined> {
  return {
    page: params.page,
    pageSize: params.pageSize,
    search: params.search && params.search.length > 0 ? params.search : undefined,
    sortBy: params.sortBy,
    sortDir: params.sortDir,
  };
}

// ============================================================================
// (A) Firma bilgileri — §10.1 (tekil: oku + güncelle; firma çağıranın kendisidir)
// ============================================================================

/**
 * Firma (Ayarlar) görünümü — api-contract §10.1 (`CompanySettingsDto`). Çağıranın
 * kendi firmasıdır (token `CompanyId`); opsiyonel alanlar `null` olabilir.
 */
export interface CompanySettingsDto {
  uuid: string;
  name: string;
  legalName: string | null;
  taxNumber: string | null;
  taxOffice: string | null;
  address: string | null;
  phone: string | null;
  email: string | null;
}

/**
 * Firma güncelleme isteği — api-contract §10.1
 * (`{ name, legalName?, taxNumber?, taxOffice?, address?, phone?, email? }`).
 * `uuid`/`companyId` istemciden **alınmaz** (token'dan). `name` boş → `400 VALIDATION_FAILED`.
 */
export interface CompanySettingsUpdateRequest {
  name: string;
  legalName?: string | null;
  taxNumber?: string | null;
  taxOffice?: string | null;
  address?: string | null;
  phone?: string | null;
  email?: string | null;
}

/** `GET /company` — çağıranın firma bilgisi (yok → `404 COMPANY_NOT_FOUND`). */
export function getCompany() {
  return api.get<CompanySettingsDto>('company');
}

/** `PUT /company` — çağıranın firmasını günceller (yalnız kendi firması). */
export function updateCompany(body: CompanySettingsUpdateRequest) {
  return api.put<CompanySettingsDto>('company', { json: body });
}

// ============================================================================
// (B) Şube bilgileri — §10.2 (tam CRUD; firmaya kapsanmış)
// ============================================================================

/**
 * Şube (Ayarlar) görünümü — api-contract §10.2 (`BranchSettingsDto`). `companyId`
 * **implicit** (daima çağıranın firması; DTO'da taşınmaz — başka firma değeri asla).
 */
export interface BranchSettingsDto {
  uuid: string;
  name: string;
  code: string | null;
  address: string | null;
  phone: string | null;
  /** UTC ISO 8601 (§4). */
  createdAt: string | null;
}

/** Şube oluşturma/güncelleme isteği — api-contract §10.2 (`{ name, code?, address?, phone? }`). */
export interface BranchSettingsWriteRequest {
  name: string;
  code?: string | null;
  address?: string | null;
  phone?: string | null;
}

/** `GET /branches` — firmanın şubeleri (sayfalı + aranabilir; yalnız kendi firması). */
export function listBranches(params: SettingsListParams = {}) {
  return api.list<BranchSettingsDto>('branches', { searchParams: listParams(params) });
}

/** `GET /branches/{uuid}` — tek şube (başka firmanınki/yok → `404 BRANCH_NOT_FOUND`). */
export function getBranch(uuid: string) {
  return api.get<BranchSettingsDto>(`branches/${uuid}`);
}

/** `POST /branches` — şube oluşturur; yeni kimlik `value.uuid` (201). `CompanyId` token'dan. */
export function createBranch(body: BranchSettingsWriteRequest) {
  return api.post<BranchSettingsDto>('branches', { json: body });
}

/** `PUT /branches/{uuid}` — şube günceller (kapsam dışı/yok → `404 BRANCH_NOT_FOUND`). */
export function updateBranch(uuid: string, body: BranchSettingsWriteRequest) {
  return api.put<BranchSettingsDto>(`branches/${uuid}`, { json: body });
}

/** `DELETE /branches/{uuid}` — **soft-delete** (kapsam dışı/yok → `404 BRANCH_NOT_FOUND`). */
export function deleteBranch(uuid: string) {
  return api.delete<Empty>(`branches/${uuid}`);
}

// ============================================================================
// (C) Saha bilgileri — §10.3 (tam CRUD; şubeye bağlı, firmaya kapsanmış)
// ============================================================================

/**
 * Saha (Ayarlar) görünümü — api-contract §10.3 (`SiteSettingsDto`). Saha bir şubeye
 * aittir (Company → Branch → Site); `branchName` görüntüleme kolaylığı içindir.
 */
export interface SiteSettingsDto {
  uuid: string;
  branchUuid: string;
  branchName: string | null;
  name: string;
  code: string | null;
  address: string | null;
  /** UTC ISO 8601 (§4). */
  createdAt: string | null;
}

/**
 * Saha oluşturma/güncelleme isteği — api-contract §10.3 (`{ branchUuid, name, code?,
 * address? }`). `branchUuid` **aynı firmada** bir şubeyi işaret etmelidir; değilse/yok
 * → `404 BRANCH_NOT_FOUND`. Güncellemede şube değişebilir (yeni şube de aynı firmada).
 */
export interface SiteSettingsWriteRequest {
  branchUuid: string;
  name: string;
  code?: string | null;
  address?: string | null;
}

/**
 * `GET /sites` — firmanın sahaları (sayfalı + aranabilir). Opsiyonel `branchUuid`
 * ile bir şubeye daraltma (§4b opsiyonel scope — verilmezse firma geneli).
 */
export function listSites(params: SettingsListParams & { branchUuid?: string } = {}) {
  const { branchUuid, ...list } = params;
  return api.list<SiteSettingsDto>('sites', {
    searchParams: {
      ...listParams(list),
      branchUuid: branchUuid && branchUuid.length > 0 ? branchUuid : undefined,
    },
  });
}

/** `GET /sites/{uuid}` — tek saha (başka firmanınki/yok → `404 SITE_NOT_FOUND`). */
export function getSite(uuid: string) {
  return api.get<SiteSettingsDto>(`sites/${uuid}`);
}

/** `POST /sites` — saha oluşturur; yeni kimlik `value.uuid` (201). `CompanyId` token'dan. */
export function createSite(body: SiteSettingsWriteRequest) {
  return api.post<SiteSettingsDto>('sites', { json: body });
}

/** `PUT /sites/{uuid}` — saha günceller (saha yok → `404 SITE_NOT_FOUND`; şube kapsam dışı → `404 BRANCH_NOT_FOUND`). */
export function updateSite(uuid: string, body: SiteSettingsWriteRequest) {
  return api.put<SiteSettingsDto>(`sites/${uuid}`, { json: body });
}

/** `DELETE /sites/{uuid}` — **soft-delete** (kapsam dışı/yok → `404 SITE_NOT_FOUND`). */
export function deleteSite(uuid: string) {
  return api.delete<Empty>(`sites/${uuid}`);
}
